import { taskActivityOrderStorageKey } from "@src/constants/localStorageKeys";

/**
 * Utility for managing task activity order preference
 *
 * This provides a persistent storage for task activity order preference
 * using localStorage, allowing it to persist across browser sessions.
 */

// Activity order types
export type TaskActivityOrder = "oldest-first" | "newest-first";

/**
 * Gets the current task activity order preference
 * @returns The current order preference, defaults to 'oldest-first'
 */
export function getTaskActivityOrder(): TaskActivityOrder {
  try {
    const savedOrder = localStorage.getItem(taskActivityOrderStorageKey);
    if (savedOrder === "newest-first") {
      return "newest-first";
    }
  } catch (error) {
    console.error("Error loading task activity order preference:", error);
  }

  // Default to oldest-first if no preference is found
  return "oldest-first";
}

/**
 * Saves task activity order preference to localStorage
 * @param order The order preference to save
 */
function saveTaskActivityOrder(order: TaskActivityOrder): void {
  try {
    localStorage.setItem(taskActivityOrderStorageKey, order);
  } catch (error) {
    console.error("Error saving task activity order preference:", error);
  }
}

/**
 * Toggles the current task activity order preference
 * @returns The new order after toggling
 */
export function toggleTaskActivityOrder(): TaskActivityOrder {
  const currentOrder = getTaskActivityOrder();
  const newOrder =
    currentOrder === "oldest-first" ? "newest-first" : "oldest-first";
  saveTaskActivityOrder(newOrder);
  return newOrder;
}
